import { Injectable, Type } from '@angular/core';
import { InfoDialogComponent } from '@app/shared/components/info-dialog/info-dialog.component';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { BootstrapSemanticVariation } from '@app/utils/bootstrap';
import { NgbModal, type NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  ConfirmationDialogFunction,
  ConfirmationDialogInput,
} from './confirmation-dialog/confirmation-dialog-input';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { DialogComponent } from './dialog-component.directive';
import { DialogRef } from './dialog-ref';
import { ModalOptions } from './modal-options';

/**
 * Extract the input type of the given dialog component
 */
type Input<T> = T extends DialogComponent<infer X, infer Y> ? X : unknown;

/**
 * Extract the output type of the given dialog component
 */
type Output<T> = T extends DialogComponent<infer X, infer Y> ? Y : unknown;

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(
    hotkeyService: HotkeyService,
    private ngbModal: NgbModal,
    private translateService: TranslateService
  ) {
    hotkeyService
      .addShortcut({
        keys: 'alt.arrowLeft',
      })
      .subscribe((event) => {
        if (this.hasOpenModals()) {
          event.preventDefault();
        }
      });
  }

  /**
   * Opens a new modal window with the specified content and supplied options.
   *
   * Content can be provided as a `TemplateRef` or a component type. If you pass a component type as content,
   * then instances of those components can be injected with an instance of the `NgbActiveModal` class. You can then
   * use `NgbActiveModal` methods to close / dismiss modals from "inside" of your component.
   *
   * Also see the [`NgbModalOptions`](#/components/modal/api#NgbModalOptions) for the list of supported options.
   */
  open<Component extends DialogComponent>(
    content: Type<Component>,
    options: NgbModalOptions = ModalOptions.DEFAULT_MODAL_OPTIONS
  ): DialogRef<Component, Input<Component>, Output<Component>> {
    const ngbModalRef = this.ngbModal.open(content, {
      ...ModalOptions.DEFAULT_MODAL_OPTIONS,
      ...options,
    });

    return new DialogRef<Component, Input<Component>, Output<Component>>(ngbModalRef);
  }

  openConfirmationDialog(
    inputs: ConfirmationDialogInput | string,
    yes?: ConfirmationDialogFunction,
    no?: ConfirmationDialogFunction
  ): DialogRef<ConfirmationDialogComponent, ConfirmationDialogInput, void> {
    if (typeof inputs === 'string') {
      inputs = {
        cancel: no,
        text: inputs,
        ok: yes,
      };
    }

    if (inputs.title == null) {
      inputs.title = 'generic.confirm';
    }

    // confirmation and cancel button have default text 'yes' and 'no' that can be overwritten.
    inputs.okText = inputs.okText ? inputs.okText : 'switch.yes';
    inputs.cancelText = inputs.cancelText ? inputs.cancelText : 'switch.no';

    return this.open(ConfirmationDialogComponent).withInput({
      ...inputs,
    });
  }

  /**
   * Open an informational dialog.
   *
   * @param content Text that is shown in the modal body
   * @param title Title that is shown in the modal header
   * @param variant Displays the header in the given variant and adds an icon for warning
   */
  public openInfo(content: string, title: string, variant?: BootstrapSemanticVariation) {
    return this.open(InfoDialogComponent).withInput({
      content,
      title,
      variant,
    });
  }

  /**
   * Open a dialog that informs the user that something has not been implemented yet.
   */
  public openNyi() {
    this.openInfo(this.translateService.instant('nyi.body'), this.translateService.instant('nyi.title'));
  }

  /**
   * Open a dialog that asks the user to confirm a delete operation.
   *
   * @param ok Called if the user confirms
   * @param title I18n key that is shown as the title of the confirmation dialog
   * @param text I18n key that is shown as the text of the confirmation dialog
   */
  public openRemovalConfirmationDialog(ok: ConfirmationDialogFunction, title?: string, text?: string) {
    return this.openConfirmationDialog({
      ok,
      text: text ?? 'delete.confirm.msg',
      title: title ?? 'generic.delete.entry',
      variant: 'danger',
    });
  }

  /**
   * Dismisses all currently displayed modal windows with the supplied reason.
   *
   * @since 3.1.0
   */
  dismissAll(reason?: any): void {
    this.ngbModal.dismissAll(reason);
  }

  /**
   * Indicates if there are currently any open modal windows in the application.
   *
   * @since 3.3.0
   */
  hasOpenModals(): boolean {
    return this.ngbModal.hasOpenModals();
  }
}
